// Port of the Revise room's model (Apps/Shared/Rooms/LibraryModel+Revise.swift): what Revise works through (`reviseUnits`:
// the manuscript's chapters as compile numbers them, else its top-level items), each unit's scenes, and `ReviseFinding` —
// everything to underline in them, pass by pass (`design-docs/Revise passes — design.md`): the last style check's findings
// that still stand (found again by `ReadingMarks`) and tense's, worked out live; quotation marks never closed or opened;
// every mistake and point of typography `TextCleanup` finds; and the consistency pass's places in the other form
// (`findVariants`, over the whole book at once). `reviseBook` does the whole manuscript, so the margin lists every
// chapter's findings. Ids survive edits elsewhere (fragment, check, passage with a little context — never an offset), so a
// dismissed finding stays dismissed. Exact: pinned to the Swift by the mac-revise fixture.
import type { OpenedProject } from '../project.ts';
import type { ArrangementTreeNode } from '../model/arrangement.ts';
import { sha256Hex } from '../hashing.ts';
import { quoteStyleOf, type QuoteStyle } from '../text/dialogueBalance.ts';
import { projectDashStyle, type DashStyle } from './dashStyle.ts';
import { checkQuotes, quoteProblemMessage } from '../text/quoteMarks.ts';
import { resolvePassage } from './readingMarks.ts';
import { checkStyle, styleText, type StyleFinding } from './style/linter.ts';
import { checkText, CLEANUP_CHECKS, usualDashStyle } from './textCleanup.ts';
import { flaggedOccurrences, findVariants, VARIANT_KINDS, type VariantOccurrence, type VariantSet } from './variants.ts';
import { projectStyleSheet } from './styleSheet.ts';
import { checkName, checkPass, QUOTES_CHECK, SPELLING_CHECK } from './checks.ts';
import type { RevisePass } from './textCleanup.ts';
import { fromScalars, toScalars } from '../text/scalars.ts';
import type { Speller } from './speller.ts';
import { spellingCandidates } from './spellingCandidates.ts';

export { QUOTES_CHECK } from './checks.ts';

export interface ReviseFinding {
  id: string;
  /** A built-in style check's raw value, a `CleanupCheck`, a `VariantKind`, `rule:<id>` or `quotes`. */
  check: string;
  checkName: string;
  fragmentID: string;
  /** Where it is now (Unicode scalars). */
  range: [number, number];
  quote: string;
  message: string;
  /** The linter's finding, for the margin and for teaching rules. */
  style?: StyleFinding;
  /** What Fix puts in its place; absent when only the writer can say. */
  replacement?: string;
  /** For a fix: a word either side, as it reads now and after ("Weird — it" → "Weird—it"). */
  preview?: { before: string; after: string };
  /** The words around it in its paragraph (stored Markdown), and where it is in them (UTF-16), for the margin's list. */
  context: { text: string; match: [number, number] };
  /** The Revise unit (chapter) it's in, and its scene's row in the contents (`reviseBook`). */
  unitID?: string;
  nodeID?: string;
  /** A consistency finding's set of variants (`ReviseBook.variants`). */
  variantSetID?: string;
  /** A spelling finding's guesses, best first. */
  guesses?: string[];
}

export const isRuleFinding = (f: ReviseFinding) => f.check.startsWith('rule:');
/** The pass it belongs to. */
export const findingPass = (f: ReviseFinding): RevisePass => checkPass(f.check);
export const isFixable = (f: ReviseFinding) => f.replacement !== undefined;

/** A scene of a unit being revised, as it reads now. */
export interface ReviseScene { id: string; title: string; text: string }

/** `ReviseFinding.preview`: the words around `range`, with `replacement` in its place. */
export function findingPreview(scalars: readonly string[], range: [number, number], replacement: string): { before: string; after: string } {
  let from = range[0], to = range[1];
  let words = 0;
  while (from > 0 && scalars[from - 1] !== '\n' && !(scalars[from - 1] === ' ' && words === 1)) {
    from--;
    if (scalars[from] !== ' ' && (from === 0 || scalars[from - 1] === ' ')) words++;
  }
  words = 0;
  while (to < scalars.length && scalars[to] !== '\n' && !(scalars[to] === ' ' && words === 1)) {
    if (scalars[to] !== ' ' && (to === range[1] || scalars[to - 1] === ' ')) words++;
    to++;
  }
  const s = scalars as string[];
  const head = fromScalars(s, from, range[0]), tail = fromScalars(s, range[1], to);
  return { before: head + fromScalars(s, range[0], range[1]) + tail, after: head + replacement + tail };
}

/** `ReviseFinding.context`: a few words either side of `range`, within its paragraph, cut at whole words ("…an
 *  ellipsis…"), and where the passage is in them (UTF-16). */
export function findingContext(scalars: readonly string[], range: [number, number], reach = 40): { text: string; match: [number, number] } {
  const s = scalars as string[];
  const lower = Math.min(range[0], s.length), upper = Math.min(Math.max(range[1], lower), s.length);
  let from = Math.max(0, lower - reach), to = Math.min(s.length, upper + reach);
  for (let k = lower - 1; k >= from; k--) if (s[k] === '\n') { from = k + 1; break; }
  for (let k = upper; k < to; k++) if (s[k] === '\n') { to = k; break; }
  const cutHead = from > 0 && s[from - 1] !== '\n';
  const cutTail = to < s.length && s[to] !== '\n';
  if (cutHead) for (let k = from; k < lower; k++) if (s[k] === ' ') { from = k + 1; break; }
  if (cutTail) for (let k = to - 1; k >= upper; k--) if (s[k] === ' ') { to = k; break; }
  const head = (cutHead ? '…' : '') + fromScalars(s, from, lower);
  const match = fromScalars(s, lower, upper);
  return { text: head + match + fromScalars(s, upper, to) + (cutTail ? '…' : ''), match: [head.length, head.length + match.length] };
}

/** `LibraryModel.reviseUnits`: the manuscript's chapters (as compile numbers them), else its top-level items. */
export function reviseUnits(project: OpenedProject): ArrangementTreeNode[] {
  const manuscript = project.manuscriptId();
  if (!manuscript) return [];
  const tree = project.arrangementTree(manuscript);
  const chapters = new Set([...project.openers()].filter(([, o]) => o.kind === 'chapter').map(([id]) => id));
  const units: ArrangementTreeNode[] = [];
  const walk = (nodes: ArrangementTreeNode[]) => {
    for (const n of nodes) {
      const id = n.node.fragmentID;
      if (id !== undefined && chapters.has(id)) units.push(n); else walk(n.children);
    }
  };
  walk(tree);
  return units.length ? units : tree;
}

/** `reviseUnit(in:)`: the unit holding the selected node, else the first. */
export function reviseUnit(units: readonly ArrangementTreeNode[], selectedNodeID: string | null): ArrangementTreeNode | null {
  if (selectedNodeID === null) return units[0] ?? null;
  const contains = (n: ArrangementTreeNode): boolean => n.node.id === selectedNodeID || n.children.some(contains);
  return units.find(contains) ?? units[0] ?? null;
}

/** A unit's documents as saved now, research notes left out, each with its row in the contents. */
function unitScenes(project: OpenedProject, unit: ArrangementTreeNode): (ReviseScene & { nodeID: string })[] {
  const collect = (n: ArrangementTreeNode): [string, string][] => [...(n.node.fragmentID !== undefined ? [[n.node.fragmentID, n.node.id] as [string, string]] : []), ...n.children.flatMap(collect)];
  const out: (ReviseScene & { nodeID: string })[] = [];
  for (const [id, nodeID] of collect(unit)) {
    const f = project.fragmentRecord(id);
    if (!f || f.kind === 'research') continue;
    out.push({ id, title: f.title, text: f.text, nodeID });
  }
  return out;
}

/** `reviseScenes`: the unit's documents as saved now, research notes left out. */
export function reviseScenes(project: OpenedProject, unit: ArrangementTreeNode): ReviseScene[] {
  return unitScenes(project, unit).map(({ id, title, text }) => ({ id, title, text }));
}

export interface ReviseFindingOptions {
  /** The last style check's findings (and tense's). */
  styleFindings?: readonly StyleFinding[] | null;
  dashStyle?: DashStyle;
  /** The marks dialogue takes: the style sheet's, else the book's (default: these scenes'). */
  quoteStyle?: QuoteStyle | null;
  language?: string | null;
  /** The consistency pass's flagged places, by fragment. */
  variants?: ReadonlyMap<string, readonly [VariantSet, VariantOccurrence][]>;
}

/** `LibraryModel.reviseFindings(in:…)`: everything to underline in these scenes — the style findings that still stand,
 *  quotation marks never closed, every mistake and point of typography `TextCleanup` finds (all of its checks: which are
 *  shown is the column's business), and the consistency pass's places. Dismissed ones included; in the order found. */
export function reviseFindings(scenes: readonly ReviseScene[], o: ReviseFindingOptions = {}): ReviseFinding[] {
  const quoteStyle = o.quoteStyle ?? quoteStyleOf(scenes.map((s) => s.text));
  const findings: ReviseFinding[] = [];
  const seen = new Map<string, number>();
  /** An id that survives edits elsewhere: the fragment, the check, and the passage with a little context (a repeat of
   *  the same passage counts up), never its offset. */
  const key = (prefix: string, scene: ReviseScene, scalars: string[], range: [number, number]) => {
    const from = Math.max(0, range[0] - 24), to = Math.min(scalars.length, range[1] + 24);
    const passage = fromScalars(scalars, from, to);
    const base = `${prefix}-${scene.id}-` + sha256Hex(`${range[1] - range[0]}|${range[0] - from}|` + passage, 8);
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return n === 0 ? base : `${base}-${n}`;
  };
  const options = { checks: CLEANUP_CHECKS, quoteStyle, dashStyle: o.dashStyle ?? 'closed', language: o.language ?? null };
  for (const scene of scenes) {
    const scalars = toScalars(scene.text);
    const context = (range: [number, number]) => findingContext(scalars, range);
    for (const f of o.styleFindings ?? []) {
      if (f.fragmentID !== scene.id) continue;
      const range = resolvePassage(f.quote, f.range, scene.text);
      if (!range) continue;
      findings.push({ id: f.id, check: f.check, checkName: f.checkName, fragmentID: scene.id, range, quote: f.quote, message: f.message, style: f, context: context(range) });
    }
    for (const p of checkQuotes(scene.text).problems) {
      if (p.kind !== 'unclosed' && p.kind !== 'unopened') continue;
      const range: [number, number] = [p.offset, p.offset + 1];
      findings.push({ id: key(QUOTES_CHECK, scene, scalars, range), check: QUOTES_CHECK, checkName: checkName(QUOTES_CHECK), fragmentID: scene.id,
        range, quote: scalars[p.offset], message: quoteProblemMessage(p), context: context(range) });
    }
    for (const f of checkText(scene.text, options).findings) {
      findings.push({
        id: key(`cleanup-${f.check}`, scene, scalars, f.range), check: f.check, checkName: checkName(f.check), fragmentID: scene.id, range: f.range,
        quote: fromScalars(scalars, f.range[0], f.range[1]), message: f.message,
        ...(f.replacement !== null ? { replacement: f.replacement, preview: findingPreview(scalars, f.range, f.replacement) } : {}),
        context: context(f.range),
      });
    }
    for (const [set, x] of o.variants?.get(scene.id) ?? []) {
      if (x.range[1] > scalars.length) continue;
      findings.push({
        id: key(`variant-${set.kind}`, scene, scalars, x.range), check: set.kind, checkName: checkName(set.kind), fragmentID: scene.id, range: x.range,
        quote: x.written, message: variantMessage(set, x),
        ...(x.alternative !== null ? { replacement: x.alternative, preview: findingPreview(scalars, x.range, x.alternative) } : {}),
        context: context(x.range), variantSetID: set.id,
      });
    }
  }
  return findings;
}

/** `LibraryModel.variantMessage`: the forms and how often the book has each, or the rule a capital breaks. */
export function variantMessage(set: VariantSet, x: VariantOccurrence): string {
  switch (set.kind) {
    case 'family-words': {
      const small = x.alternative !== null && x.alternative === x.alternative.toLowerCase();
      return small ? `After a word like “my” or “a”, “${x.written}” is a word, not a name: it takes a small letter.` : `Used as a name, “${x.written}” takes a capital.`;
    }
    case 'numbers': {
      const words = set.counts.words ?? 0, digits = set.counts.digits ?? 0;
      return `The book spells numbers to one hundred out (${words} in words, ${digits} in digits). Books usually do.`;
    }
    default: {
      const counts = set.forms.map((f) => `“${f}” ${set.counts[f] ?? 0}×`).join(', ');
      if (set.kept !== null && set.kind !== 'names') return `The style sheet keeps “${set.kept}”. The book has ${counts}.`;
      return `The book has ${counts}.`;
    }
  }
}

/** The ids of findings these scenes had once (`pruneDismissed`'s prefixes): quotation marks, mistakes, typography,
 *  consistency and spelling, and the ids of before passes ("slips-…"). Style findings keep theirs (a check may not have run). */
export function findingPrefixes(sceneIDs: readonly string[]): string[] {
  return sceneIDs.flatMap((id) => [
    `quotes-${id}-`, `slips-${id}-`, ...CLEANUP_CHECKS.flatMap((c) => [`slips-${c}-${id}-`, `cleanup-${c}-${id}-`]),
    ...VARIANT_KINDS.map((k) => `variant-${k}-${id}-`), `${SPELLING_CHECK}-${id}-`,
  ]);
}

/** The manuscript as Revise ▸ Checks reads it (`ReviseBook`): each chapter's scenes, and everything to underline, in
 *  reading order. Plain objects, so it crosses from the Revise worker. */
export interface ReviseBook {
  /** Revise's units (`reviseUnits`) by node id, in order. */
  units: string[];
  /** Each unit's scenes, by unit. */
  scenes: Record<string, ReviseScene[]>;
  /** Everything to underline, dismissed ones left out. */
  findings: ReviseFinding[];
  /** The consistency pass's sets, by id, and the scenes their occurrences' `text` indexes. */
  variants: Record<string, VariantSet>;
  sceneOrder: string[];
  /** Dismissed ids that match nothing any more (the passage was rewritten): forget them, so the list doesn't grow for ever. */
  staleDismissed: string[];
}

export interface ReviseBookOptions {
  /** The last style check's findings (null: none has run). Tense's are worked out here, live, when it's switched on. */
  styleFindings?: readonly StyleFinding[] | null;
  /** The dash style to check against (default: the project's, else the one the counted text mostly uses). */
  dashStyle?: DashStyle;
  /** The ids the writer chose to leave, on this device (`dismissedFindings-<project>`). */
  dismissed?: ReadonlySet<string>;
  /** The spelling check: a checker for the project's language, and the words the writer knows (`spellingWords`: the
   *  glossary, the cast, the personal dictionary). None: no spelling findings. */
  spelling?: { speller: Speller; known: Iterable<string> };
}

/** Each checker's verdicts, by word: its guesses when it doesn't know it, null when it does (`spellingVerdicts`). */
const verdicts = new WeakMap<Speller, Map<string, string[] | null>>();

/** `LibraryModel.spellingFindings`: each distinct word put to the checker once (kept with the checker). */
export function spellingFindings(scenes: readonly ReviseScene[], speller: Speller, known: Iterable<string>): ReviseFinding[] {
  const candidates = spellingCandidates(scenes.map((s) => s.text), known);
  let cache = verdicts.get(speller);
  if (!cache) verdicts.set(speller, (cache = new Map()));
  const seen = new Map<string, number>();
  const out: ReviseFinding[] = [];
  for (const w of candidates.words) {
    let guesses = cache.get(w.word);
    if (guesses === undefined) {
      guesses = speller.correct(w.word) ? null : speller.suggest(w.word, 4);
      cache.set(w.word, guesses);
    }
    const scene = scenes[w.text];
    if (guesses === null || !scene) continue;
    const scalars = toScalars(scene.text);
    if (w.range[1] > scalars.length) continue;
    const from = Math.max(0, w.range[0] - 24), to = Math.min(scalars.length, w.range[1] + 24);
    const base = `${SPELLING_CHECK}-${scene.id}-` + sha256Hex(w.word + '|' + fromScalars(scalars, from, to), 8);
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    const uses = candidates.uses.get(w.word.toLowerCase()) ?? 1;
    out.push({
      id: n === 0 ? base : `${base}-${n}`, check: SPELLING_CHECK, checkName: 'Spelling', fragmentID: scene.id, range: w.range, quote: w.word,
      message: `“${w.word}” isn't a word the spelling check knows. ` + (uses === 1 ? "It's in the book once." : "It's in the book twice."),
      guesses, context: findingContext(scalars, w.range),
    });
  }
  return out;
}

/** `LibraryModel.reviseBook`: every unit's scenes as saved now, and what to underline in them. */
export function reviseBook(project: OpenedProject, o: ReviseBookOptions = {}): ReviseBook {
  const units = reviseUnits(project);
  const parts = units.map((unit) => ({ unit: unit.node.id, scenes: unitScenes(project, unit) }));
  const all = parts.flatMap((p) => p.scenes);
  const record = project.record;
  const builtIns = record.settings.style && typeof record.settings.style === 'object' ? (record.settings.style as Record<string, unknown>) : {};
  const names = project.entities().flatMap((e) => [e.name, ...e.aliases]);
  // Tense runs here, live, over the whole book (it's word lists); the rest of the style checks on Check.
  const style = (o.styleFindings ?? []).filter((f) => f.check !== 'tense');
  if (builtIns.tense === true) style.push(...checkStyle(all.map((s) => styleText(s.id, s.title, s.text)), ['tense'], names));
  const sheet = projectStyleSheet(record);
  const dashStyle = o.dashStyle ?? projectDashStyle(record) ?? usualDashStyle(project.counted(project.targets().countAllText).map((id) => project.fragment(id)?.text ?? ''));
  // The book's dialogue marks, unless the style sheet says.
  const quoteStyle = sheet.dialogueQuotes ?? quoteStyleOf(all.map((s) => s.text));
  // Consistency reads the whole book at once; each place it flags goes to its scene.
  const sets = findVariants(all.map((s) => s.text), { names, choices: sheet.choices, numbers: sheet.numbers, language: record.locale });
  const flagged = new Map<string, [VariantSet, VariantOccurrence][]>();
  for (const set of sets) {
    for (const x of flaggedOccurrences(set)) {
      const id = all[x.text].id;
      flagged.set(id, [...(flagged.get(id) ?? []), [set, x]]);
    }
  }
  // Spelling reads the whole book at once too (a word used three times anywhere is the writer's own).
  const spelling = new Map<string, ReviseFinding[]>();
  if (o.spelling) {
    for (const f of spellingFindings(all, o.spelling.speller, o.spelling.known)) spelling.set(f.fragmentID, [...(spelling.get(f.fragmentID) ?? []), f]);
  }
  const findings: ReviseFinding[] = [];
  for (const part of parts) {
    // Reading order: scene by scene, then by where each starts (spelling's after the others at the same place).
    const order = new Map<string, number>();
    part.scenes.forEach((s, i) => { if (!order.has(s.id)) order.set(s.id, i); });
    const node = new Map(part.scenes.map((s) => [s.id, s.nodeID]));
    const found = [
      ...reviseFindings(part.scenes, { styleFindings: style, dashStyle, quoteStyle, language: record.locale, variants: flagged }),
      ...[...new Set(part.scenes.map((s) => s.id))].flatMap((id) => spelling.get(id) ?? []),
    ]
      .map((f) => ({ ...f, unitID: part.unit, nodeID: node.get(f.fragmentID) }))
      .sort((a, b) => (order.get(a.fragmentID) ?? 0) - (order.get(b.fragmentID) ?? 0) || a.range[0] - b.range[0]);
    findings.push(...found);
  }
  const dismissed = o.dismissed ?? new Set<string>();
  const ids = new Set(findings.map((f) => f.id));
  const prefixes = findingPrefixes(all.map((s) => s.id));
  return {
    units: parts.map((p) => p.unit),
    scenes: Object.fromEntries(parts.map((p) => [p.unit, p.scenes.map(({ id, title, text }) => ({ id, title, text }))])),
    findings: findings.filter((f) => !dismissed.has(f.id)),
    variants: Object.fromEntries(sets.map((s) => [s.id, s])),
    sceneOrder: all.map((s) => s.id),
    staleDismissed: [...dismissed].filter((d) => !ids.has(d) && prefixes.some((p) => d.startsWith(p))),
  };
}

/** The margin comment "Add to margin" posts for a finding: a style finding as "Style linter" (`style-<id>`, "Check:
 *  message"), anything else under its check's name with its own id. See `edits.ts` (`addFindingsToMargin`). */
export function marginCommentFor(f: ReviseFinding): { author: string; comment: { id: string; fragmentID: string; range: [number, number]; body: string } } {
  if (f.style) return { author: STYLE_LINTER_AUTHOR, comment: styleComment(f.style) };
  return { author: f.checkName, comment: { id: f.id, fragmentID: f.fragmentID, range: f.range, body: f.message } };
}

export const STYLE_LINTER_AUTHOR = 'Style linter';
export const CONTINUITY_AUTHOR = 'Continuity check';

/** `addStyleToMargin`'s comment for a style finding. */
export const styleComment = (f: StyleFinding) => ({ id: 'style-' + f.id, fragmentID: f.fragmentID, range: f.range, body: `${f.checkName}: ${f.message}` });

/** `addToMargin(_ findings: [ContinuityFinding])`'s comment: `continuity-<id>`, the message as it is. */
export const continuityComment = (f: { id: string; fragmentID: string; range: [number, number]; message: string }) =>
  ({ id: 'continuity-' + f.id, fragmentID: f.fragmentID, range: f.range, body: f.message });
